import React, { useEffect, useMemo, useState } from 'react';
import { getBuildingsList } from '../../../../utils/api';
import { showErrorAlert } from '../../../../utils/alerts';
import { getDoorsByBuildingId } from '../../../../utils/services/doorService';
import { useReaderTelemetrySocket } from './hooks/useReaderTelemetrySocket';
import ReaderReboot from './ReaderReboot';
import ReaderConfig from './ReaderConfig';
import './ReadersModule.css';

const TABS = [
  { key: 'reboot', label: 'Reinicio' },
  { key: 'config', label: 'Configuración' },
];

const ReadersModule = () => {
  const [activeTab, setActiveTab] = useState('reboot');
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedLocalId, setSelectedLocalId] = useState('');
  const [doors, setDoors] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingDoors, setLoadingDoors] = useState(false);

  useEffect(() => {
    const loadBuildings = async () => {
      try {
        setLoadingBuildings(true);
        const data = await getBuildingsList();
        setBuildings(data);
      } catch (error) {
        showErrorAlert('Error', error.message);
      } finally {
        setLoadingBuildings(false);
      }
    };
    loadBuildings();
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) {
      setDoors([]);
      return;
    }

    const loadDoors = async () => {
      try {
        setLoadingDoors(true);
        const response = await getDoorsByBuildingId(selectedBuildingId);
        setDoors(response.doors || response);
      } catch (error) {
        showErrorAlert('Error', error.message);
      } finally {
        setLoadingDoors(false);
      }
    };

    loadDoors();
  }, [selectedBuildingId]);

  // Real-time reader telemetry & status listener via Custom Hook (SOLID - SRP)
  useReaderTelemetrySocket({ selectedBuildingId, setDoors });

  // Reset selected reader when building changes
  useEffect(() => {
    setSelectedLocalId('');
  }, [selectedBuildingId]);

  // Ensure selected reader is still valid when doors list updates
  useEffect(() => {
    if (selectedLocalId && !doors.some((d) => String(d.localId) === String(selectedLocalId))) {
      setSelectedLocalId('');
    }
  }, [doors, selectedLocalId]);

  const sortedBuildings = useMemo(() => {
    return [...buildings].sort((a, b) => a.name.localeCompare(b.name));
  }, [buildings]);

  const sortedDoors = useMemo(() => {
    return [...doors].sort((a, b) => a.localId - b.localId);
  }, [doors]);

  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);

  const sharedProps = {
    buildingId: selectedBuildingId,
    doors: sortedDoors,
    loadingDoors,
    selectedBuilding,
    selectedLocalId,
    setSelectedLocalId,
  };

  return (
    <>

      <div className="readers-module-wrapper">

        <div className="readers-layout-container">
          <aside className="readers-sidebar">
            <div className="mb-3">
              <h2 className="text-white font-weight-bold">Administrar Lectoras</h2>
            </div>
            <div className="tabs-container">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(tab.key);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="readers-filters">
              <div className="d-flex align-items-center mb-3">
                <label htmlFor="building-select" className="text-white small fw-semibold mr-3 mb-0 text-nowrap reader-label">Edificio:</label>
                <div className="skew-input select input-shape flex-grow-1 mb-0">
                  {loadingBuildings ? (
                    <select id="building-select" disabled>
                      <option>Cargando edificios...</option>
                    </select>
                  ) : (
                    <select
                      id="building-select"
                      value={selectedBuildingId}
                      onChange={(e) => {
                        setSelectedBuildingId(e.target.value);
                      }}
                    >
                      <option value="">Selecciona un edificio</option>
                      {sortedBuildings.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {activeTab === 'config' && (
                <div className="d-flex align-items-center mb-4">
                  <label htmlFor="reader-select" className="text-white small fw-semibold mr-3 mb-0 text-nowrap reader-label">Lectora:</label>
                  <div className="skew-input select input-shape flex-grow-1 mb-0">
                    {!selectedBuildingId ? (
                      <select id="reader-select" disabled>
                        <option value="">Selecciona un edificio primero</option>
                      </select>
                    ) : loadingDoors ? (
                      <select id="reader-select" disabled>
                        <option>Cargando lectoras...</option>
                      </select>
                    ) : sortedDoors.length === 0 ? (
                      <select id="reader-select" disabled>
                        <option>No hay lectoras disponibles</option>
                      </select>
                    ) : (
                      <select id="reader-select" value={selectedLocalId} onChange={(e) => setSelectedLocalId(e.target.value)}>
                        <option value="">Selecciona una lectora</option>
                        {sortedDoors.map((door) => (
                          <option key={door.id} value={door.localId}>
                            {door.name || `Lectora ${door.localId}`} (ID: {door.localId})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>

          <main className={`readers-content ${activeTab === 'config' ? 'readers-content--config' : ''}`}>
            {activeTab === 'reboot' && (
              <ReaderReboot buildingId={selectedBuildingId} doors={sortedDoors} loadingDoors={loadingDoors} />
            )}
            {activeTab === 'config' && <ReaderConfig {...sharedProps} />}
          </main>
        </div>
      </div>
    </>
  );
};

export default ReadersModule;
